// Enrolment photos for tiles, fetched once per sha through the device-signed API and held as
// object URLs in memory. Offline (PMU-270): each is also kept on the device, sealed with the
// device's own key (shared/offline.ts), so faces still show after a restart with no internet.
import { useEffect, useState } from "react";
import { blobGet, blobPut, offlineEnabled } from "../shared/offline.ts";

export interface PhotoSource {
  /** A ready object URL, or undefined while loading or when there is no photo. */
  peek(sha: string | null | undefined): string | undefined;
  load(sha: string): Promise<string | undefined>;
}

export class PhotoCache implements PhotoSource {
  private urls = new Map<string, string>();
  private pending = new Map<string, Promise<string | undefined>>();
  private fetchBlob: (sha: string) => Promise<Blob | null>;
  constructor(fetchBlob: (sha: string) => Promise<Blob | null>) { this.fetchBlob = fetchBlob; }
  peek(sha: string | null | undefined) { return sha ? this.urls.get(sha) : undefined; }
  load(sha: string) {
    const have = this.urls.get(sha);
    if (have) return Promise.resolve(have);
    let p = this.pending.get(sha);
    if (!p) {
      p = this.fetchBlob(sha).catch(() => null).then(async (b) => {
        if (b && offlineEnabled()) void blobPut(`photo:${sha}`, b);
        if (!b && offlineEnabled()) b = await blobGet(`photo:${sha}`);
        if (!b) return undefined;
        const url = URL.createObjectURL(b);
        this.urls.set(sha, url);
        return url;
      }, () => undefined).finally(() => this.pending.delete(sha));
      this.pending.set(sha, p);
    }
    return p;
  }
  /** Frees every URL, e.g. when the device is wiped. */
  clear() { for (const u of this.urls.values()) URL.revokeObjectURL(u); this.urls.clear(); }
}

export const noPhotos: PhotoSource = { peek: () => undefined, load: async () => undefined };

export function usePhoto(src: PhotoSource, sha: string | null | undefined): string | undefined {
  const [url, setUrl] = useState(() => src.peek(sha));
  useEffect(() => {
    if (!sha) { setUrl(undefined); return; }
    const ready = src.peek(sha);
    if (ready) { setUrl(ready); return; }
    let live = true;
    src.load(sha).then((u) => { if (live) setUrl(u); });
    return () => { live = false; };
  }, [src, sha]);
  return url;
}
